import type { BackgroundResult } from '../media/backgroundProcessor';
import type { DevicePrefs } from './devicePrefs';
import type { BlurStrength, VideoFilter } from '../media/videoEffects';
import type { BackgroundMode, BackgroundQualitySetting } from '../media/backgroundProcessor';
import type { FaceMaskId } from '../media/faceMasks';

/**
 * The live in-call camera-effects switching machine.
 *
 * The effects dialog writes camera choices (background mode/quality, blur strength,
 * color filter) through the preference store; the room page's store subscription feeds
 * each change here. The machine keeps ONE original camera capture alive (never stopped
 * mid-session) and drives the LiveKit camera publication through `replaceTrack` - the
 * upstream swaps to the processed canvas track (or back to the plain capture) with no
 * rejoin and no republish; the publication's `source: Track.Source.Camera` stamp never
 * changes because the publication object itself is untouched.
 *
 * Decision table per applied request:
 *  - nothing published (viewer, camera off): no-op, the choice persists for the next
 *    publish (handleToggleCam and the media-handover re-publish re-run the machine);
 *  - effects fully off (raw mode + no filter + no mask): swap back to the original
 *    capture, stop the running processor with `stop()` (it releases everything
 *    EXCEPT the capture - the capture becomes the published track again);
 *  - a running processor whose quality level matches: live setters only
 *    (setMode / setBlurStrength / setFilter / setMask) - no canvas rebuild, no
 *    track swap;
 *  - otherwise (no processor yet, or a quality change): start a fresh processor from
 *    the original capture, swap it in via replaceTrack, stop the previous processor.
 *    A start or swap failure keeps the previous track published and reports the error
 *    (the caller surfaces the existing control-bar toast pattern).
 *
 * Racing requests are versioned with a sequence number: a slower start that finishes
 * after a newer request arrived stops its processor and loses. Everything browser- and
 * SDK-specific is injected ({@link CameraEffectsMachineDeps}), so the orchestration
 * runs under jsdom against recording fakes.
 */

/** One camera-effects configuration the machine can be asked to apply. */
export interface CameraEffectsRequest {
  readonly mode: BackgroundMode;
  readonly filter: VideoFilter;
  readonly strength: BlurStrength;
  readonly quality: BackgroundQualitySetting;
  readonly mask: FaceMaskId;
}

/** Builds the request for the machine from the stored preferences. */
export function effectsRequestFromPrefs(prefs: DevicePrefs): CameraEffectsRequest {
  return {
    mode: prefs.bgMode,
    filter: prefs.videoFilter,
    strength: prefs.blurStrength,
    quality: prefs.bgQuality,
    mask: prefs.mask,
  };
}

/** True when the two requests differ in any machine-observable field. */
export function requestsDiffer(
  a: CameraEffectsRequest | null,
  b: CameraEffectsRequest | null,
): boolean {
  if (a === null || b === null) {
    return a !== b;
  }
  return (
    a.mode !== b.mode ||
    a.filter !== b.filter ||
    a.strength !== b.strength ||
    a.quality !== b.quality ||
    a.mask !== b.mask
  );
}

/**
 * Structural slice of the LiveKit camera publication the machine needs (the local
 * LocalTrackPublication's LocalTrack wrapper): the currently published media stream
 * track and the sender-level swap. Tests inject recording doubles.
 */
export interface CameraPublicationHandle {
  readonly mediaStreamTrack: MediaStreamTrack | null;
  replaceTrack(track: MediaStreamTrack): Promise<void>;
}

/** What the machine currently holds, for the dev-only e2e bridge. */
export interface CameraFxSnapshot {
  readonly request: CameraEffectsRequest | null;
  /** True when a processed canvas track is published (false on the raw path). */
  readonly processed: boolean;
}

export interface CameraEffectsMachineDeps {
  /** The live camera publication handle; null when no camera track is published. */
  readonly publication: () => CameraPublicationHandle | null;
  /**
   * Starts a processed outcome for the request; resolves null when processing is
   * unavailable (engine failure, too-slow machine). The machine then keeps the
   * previous track published and reports the failure.
   */
  readonly startProcessor: (
    cameraTrack: MediaStreamTrack,
    request: CameraEffectsRequest,
  ) => Promise<BackgroundResult | null>;
  /** Reports a failed apply (the caller surfaces the control-bar error toast). */
  readonly onError: (err: unknown) => void;
}

export interface CameraEffectsMachine {
  /** Applies the request to the live publication (idempotent, race-safe). */
  apply(request: CameraEffectsRequest): Promise<void>;
  /**
   * Seeds the ORIGINAL camera capture for a fresh session (the prejoin handover's
   * processor input; null clears). Without the seed the machine could mistake the
   * published canvas track for the capture and feed the composite back into itself.
   */
  seedCapture(track: MediaStreamTrack | null): void;
  /**
   * Releases everything (running processor AND the held capture). The machine stays
   * usable: the next apply re-resolves the capture from the publication - that is how
   * effects survive a media handover (the fresh Room publishes a fresh capture).
   */
  dispose(): void;
  /** The current state for the dev-only e2e bridge. */
  snapshot(): CameraFxSnapshot;
}

/** Live-track narrowing: null for absent/ended tracks (device unplug, teardown). */
function liveTrack(track: MediaStreamTrack | null | undefined): MediaStreamTrack | null {
  return track !== null && track !== undefined && track.readyState === 'live' ? track : null;
}

export function createCameraEffectsMachine(deps: CameraEffectsMachineDeps): CameraEffectsMachine {
  let capture: MediaStreamTrack | null = null;
  let processor: BackgroundResult | null = null;
  let request: CameraEffectsRequest | null = null;
  let seq = 0;

  /**
   * Resolves the ORIGINAL camera capture to feed processors. Priority: the machine's
   * held capture, the current processor's input, the published track - but never the
   * processor's own canvas output (a canvas fed back through the pipeline would
   * compound the treatment every rebuild).
   */
  function resolveCapture(publication: CameraPublicationHandle): MediaStreamTrack | null {
    const held = liveTrack(capture);
    if (held !== null) {
      return held;
    }
    const fromProcessor = liveTrack(processor?.cameraTrack ?? null);
    if (fromProcessor !== null) {
      capture = fromProcessor;
      return fromProcessor;
    }
    const published = liveTrack(publication.mediaStreamTrack);
    if (published !== null && published !== (processor?.track ?? null)) {
      capture = published;
      return published;
    }
    return null;
  }

  return {
    async apply(next: CameraEffectsRequest): Promise<void> {
      seq += 1;
      const ticket = seq;
      const publication = deps.publication();
      if (publication === null) {
        // Nothing live to apply to (viewer token, camera unpublished): the choice
        // stays stored; the next publish path re-runs the machine.
        return;
      }
      const needsProcessing =
        next.mode !== 'none' || next.filter !== 'none' || next.mask !== 'none';

      // Everything off: back to the plain capture, processor released. stop() keeps
      // the capture alive - it is the published track again after the swap.
      if (!needsProcessing) {
        const current = processor;
        if (current === null) {
          request = next;
          return;
        }
        const raw = resolveCapture(publication);
        try {
          if (raw !== null) {
            await publication.replaceTrack(raw);
          }
        } catch (err: unknown) {
          deps.onError(err);
          return;
        }
        if (seq !== ticket) {
          return; // a newer apply owns the outcome now
        }
        current.releaseExceptCapture();
        processor = null;
        request = next;
        return;
      }

      // A running processor at the same quality level absorbs everything live:
      // mode, strength, filter and mask are per-frame decisions, no rebuild, no
      // swap (a first mask activation starts the landmark engine inside the
      // processor - still no canvas rebuild, no track swap).
      const current = processor;
      if (current !== null && request !== null && request.quality === next.quality) {
        current.setMode(next.mode);
        current.setBlurStrength(next.strength);
        current.setFilter(next.filter);
        current.setMask(next.mask);
        request = next;
        return;
      }

      // (Re)build: fresh processor from the ORIGINAL capture, swap it in, release
      // the previous one (its engine and canvas die; the capture survives - the new
      // processor consumes it and takes over the leave-time dispose duty).
      const input = resolveCapture(publication);
      if (input === null) {
        deps.onError(new Error('no live camera capture available for effects'));
        return;
      }
      let started: BackgroundResult | null = null;
      try {
        started = await deps.startProcessor(input, next);
      } catch (err: unknown) {
        deps.onError(err);
        return;
      }
      if (seq !== ticket) {
        started?.releaseExceptCapture(); // superseded while starting: the newer request re-decides
        return;
      }
      if (started === null) {
        deps.onError(new Error('camera effects are unavailable on this machine'));
        return;
      }
      const previous = processor;
      try {
        await publication.replaceTrack(started.track);
      } catch (err: unknown) {
        started.releaseExceptCapture();
        deps.onError(err);
        return;
      }
      if (seq !== ticket) {
        started.releaseExceptCapture(); // a newer apply swapped after us; it owns the publication
        return;
      }
      previous?.releaseExceptCapture();
      processor = started;
      request = next;
    },

    seedCapture(track: MediaStreamTrack | null): void {
      capture = track;
    },

    dispose(): void {
      seq += 1;
      processor?.dispose();
      processor = null;
      request = null;
      // The held capture may be the prejoin's handover capture (no SDK owner): stop
      // it here - leave paths and media handovers both want the light off. Stopping
      // an already-stopped or SDK-owned capture is harmless (idempotent, and the
      // disconnect that follows stops the SDK captures anyway).
      capture?.stop();
      capture = null;
    },

    snapshot(): CameraFxSnapshot {
      return { request, processed: processor !== null };
    },
  };
}

/**
 * Dev-only e2e seam: exposes the machine's current state to Playwright page evaluation
 * (`window.__vcCamFx()`). Stripped from production builds by the import.meta.env.DEV
 * gate, like the microphone bridge in micEffects.ts.
 */
export function exposeCameraFxForTests(read: () => CameraFxSnapshot | null): void {
  if (import.meta.env.DEV !== true) {
    return;
  }
  window.__vcCamFx = (): CameraFxSnapshot | null => read();
}

declare global {
  interface Window {
    /** Dev/e2e only: the in-call camera-effects machine state (null when inactive). */
    __vcCamFx?: () => CameraFxSnapshot | null;
  }
}
